import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import Button from '../../../../../../components/global/Button';
import Spinner from '../../../../../../components/global/Spinner';
import AlbumSearchResultListing from '../AlbumSearchResultListing';
import JoinCodeResultListing from '../JoinCodeResultListing';
import UserSearchResultListing from '../UserSearchResultListing';
import { SEARCH_MODES } from '../AlbumSearchForm/AlbumSearchForm';
import NonResult from '../NonResult';
import NullState from '../NullState';
import ProDirectoryResultListing from '../ProDirectoryResultListing/ProDirectoryResultListing';

const propTypes = {
  hasQuery: PropTypes.bool.isRequired,
  hasError: PropTypes.bool.isRequired,
  searchQuery: PropTypes.object.isRequired,
  hierarchySearch: PropTypes.object,
  joinCodeAlbum: PropTypes.object,
  joinCodeAlbumFolder: PropTypes.object,
  accountSearchResults: PropTypes.object,
  proDirectoryResults: PropTypes.object,
  loadMore: PropTypes.func,
  isLoading: PropTypes.bool,
  currentSearchMode: PropTypes.string,
  hasResults: PropTypes.bool,
};

const Results = ({
  hasQuery,
  hasError,
  searchQuery,
  hierarchySearch,
  joinCodeAlbum,
  joinCodeAlbumFolder,
  accountSearchResults,
  proDirectoryResults,
  loadMore,
  isLoading,
  currentSearchMode,
  hasResults,
}) => {
  const hasMoreResults = (() => {
    // Determine if pagination is required across the three queries that support it.
    if (
      currentSearchMode === SEARCH_MODES.JOIN_CODE ||
      currentSearchMode === SEARCH_MODES.EVENTS ||
      currentSearchMode === SEARCH_MODES.LOCATION
    ) {
      const hierarchySearchResults = hierarchySearch?.records ?? [];
      const hierarchyResultsAvailable = hierarchySearch?.total ?? 0;
      return (
        hierarchySearchResults.length > 0 &&
        hierarchySearchResults.length < hierarchyResultsAvailable
      );
    }

    if (currentSearchMode === SEARCH_MODES.USER) {
      const userSearchResults = accountSearchResults?.records ?? [];
      const userSearchResultsAvailable = accountSearchResults?.total ?? 0;
      return (
        userSearchResults.length > 0 &&
        userSearchResults.length < userSearchResultsAvailable
      );
    }

    if (currentSearchMode === SEARCH_MODES.PRO_DIRECTORY) {
      const proDirectoryResultRecords = proDirectoryResults?.records ?? [];
      const proDirectoryResultTotal = proDirectoryResults?.total ?? 0;

      return (
        proDirectoryResultRecords.length > 0 &&
        proDirectoryResultRecords.length < proDirectoryResultTotal
      );
    }

    return false;
  })();

  const hasJoinCodeResult = joinCodeAlbum || joinCodeAlbumFolder;

  // What to display under what conditions.
  //
  // - no query                                                 -> show searchMode specific null states
  // - any query AND searching                                  -> show spinner only
  // - any query AND not searching AND no results AND no error  -> show 'no results'
  // - any query AND not searching AND error                    -> show error

  if (isLoading && hasQuery && !hasResults) {
    return (
      <div className="text-center mt-4">
        <Spinner />
      </div>
    );
  }

  if (!hasQuery && !isLoading) {
    return <NullState searchMode={currentSearchMode} />;
  }

  if (hasError && hasQuery && !isLoading) {
    return (
      <NonResult
        asset="/waldo-sweating.png"
        title="Whoops! Waldo had a problem making that search."
      />
    );
  }

  if (!hasError && hasQuery && !hasResults && !isLoading) {
    return <NonResult title="No results" />;
  }

  const loadMoreBtn = (
    <Fragment>
      {hasMoreResults && (
        <div className="row">
          <div className="col-12">
            <Button
              onClick={loadMore}
              btnStyle="yellow"
              className="load-more-button"
              disabled={isLoading}
            >
              {isLoading ? 'Loading…' : 'View More'}
            </Button>
          </div>
        </div>
      )}
    </Fragment>
  );

  if (hasQuery && !hasError && hasResults) {
    switch (currentSearchMode) {
      case SEARCH_MODES.LOCATION:
      case SEARCH_MODES.JOIN_CODE:
      case SEARCH_MODES.EVENTS:
        return (
          <Fragment>
            {!hasJoinCodeResult && (
              <AlbumSearchResultListing
                nameSearchField={searchQuery.name || ''}
                searchResults={hierarchySearch?.records ?? []}
              />
            )}
            {hasJoinCodeResult && (
              <JoinCodeResultListing
                joinCodeItem={joinCodeAlbum || joinCodeAlbumFolder}
              />
            )}
            {loadMoreBtn}
          </Fragment>
        );
      case SEARCH_MODES.USER:
        return (
          <Fragment>
            <UserSearchResultListing users={accountSearchResults?.records} />
            {loadMoreBtn}
          </Fragment>
        );
      case SEARCH_MODES.PRO_DIRECTORY:
        return (
          <Fragment>
            <ProDirectoryResultListing
              proDirectoryRecords={proDirectoryResults?.records}
            />
            {loadMoreBtn}
          </Fragment>
        );
      default:
        return null;
    }
  }

  return null;
};

Results.propTypes = propTypes;

export default Results;
